import { useState, useCallback } from 'react'
import { Toast, ToastType } from '@/components/ui/Toast'
import { createPortal } from 'react-dom'

interface ToastConfig {
  message: string
  type?: ToastType
  duration?: number
}

export function useToast() {
  const [toasts, setToasts] = useState<Array<ToastConfig & { id: number }>>([])

  const showToast = useCallback((config: ToastConfig) => {
    const id = Date.now()
    setToasts(prev => [...prev, { ...config, id }])
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const ToastContainer = useCallback(() => {
    if (typeof window === 'undefined' || toasts.length === 0) return null

    return createPortal(
      <div className="fixed top-20 right-4 z-[9999] space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>,
      document.body
    )
  }, [toasts, removeToast])

  return {
    showToast,
    ToastContainer,
    success: (message: string, duration?: number) => showToast({ message, type: 'success', duration }),
    error: (message: string, duration?: number) => showToast({ message, type: 'error', duration }),
    warning: (message: string, duration?: number) => showToast({ message, type: 'warning', duration }),
    info: (message: string, duration?: number) => showToast({ message, type: 'info', duration })
  }
}
